import {
  AfterViewInit,
  Component,
  ElementRef,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CartService } from '../../../core/services/cart/cart.service';
import { ResidentCart } from '../../models/cart.model';
import { DepartmentWithResidents } from '../../models/department.model';
import { TranslateService } from '@ngx-translate/core';
import { Product } from '../../models/product.model';
import { ProductsService } from '../../../core/services/products';
import { take, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { Resident } from '../../models/resident.model';
import { SnackbarService } from '../../../core/services/utils/snackbar.service';
import { ListSectionComponent } from './list-section/list-section.component';
import { CartSectionComponent } from './cart-section/cart-section.component';
import { ConfirmationDialogProps } from '../../../core/types/interfaces';
import { ModalCloseButtonComponent } from '../modal-close-button/modal-close-button.component';
import { ProfileAvatarComponent } from '../profile-avatar/profile-avatar.component';
import { ResidentBudgetComponent } from '../resident-budget/resident-budget.component';
import { SepaCheckComponent } from '../sepa-check/sepa-check.component';
import { handleGender } from '../../../core/services/utils/helpers';
import { ShopSelectionStore } from '../../../core/state/shop-selection.store';
import { ShopSelectionService } from '../../../core/services/shop-selection/shop-selection.service';

@Component({
  selector: 'app-list-of-needs-dialog',
  templateUrl: './list-of-needs-dialog.component.html',
  styleUrls: ['./list-of-needs-dialog.component.scss'],
  imports: [
    MatIconModule,
    MatSelectModule,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ModalCloseButtonComponent,
    ProfileAvatarComponent,
    ResidentBudgetComponent,
    SepaCheckComponent,
    ListSectionComponent,
    CartSectionComponent,
  ],
  providers: [
    {
      provide: LOCALE_ID,
      useValue: 'de-DE',
    },
  ],
})
export class ListOfNeedsDialogComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  residentControl: FormControl;
  residentCart: ResidentCart;
  selectedProducts: {
    product: Product | null;
    quantity: number;
    valid: boolean;
  }[] = [];
  listTotal = 0;
  listNumberOfItems = 0;
  selectedDepartment: DepartmentWithResidents | null = null;
  isStickyTopStuck = false;
  previousResident: Resident;
  suppressChange = false;
  private destroy$ = new Subject<void>();
  private stickyObserver?: IntersectionObserver;
  @ViewChild('listOfNeedsDialog', { static: false })
  private listSection!: ElementRef<HTMLDivElement>;
  @ViewChild('stickySentinel', { static: false })
  private stickySentinel!: ElementRef<HTMLDivElement>;

  constructor(
    private shopSelectionStore: ShopSelectionStore,
    private shopSelectionService: ShopSelectionService,
    private dialogRef: MatDialogRef<ListOfNeedsDialogComponent>,
    private cartService: CartService,
    private translateService: TranslateService,
    private productsService: ProductsService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService
  ) {
    this.residentControl = new FormControl();
  }

  ngOnInit() {
    this.loadSelectedDepartment();
    this.setupResidentControl();
    this.loadInitialResident();
    this.setupBackdropClick();
  }

  ngAfterViewInit(): void {
    this.setupStickyObserver();
  }

  private loadSelectedDepartment(): void {
    this.shopSelectionStore.department$
      .pipe(takeUntil(this.destroy$))
      .subscribe(department => {
        this.selectedDepartment = department;
      });
  }

  private setupResidentControl(): void {
    this.residentControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(resident => {
        if (this.suppressChange) {
          return;
        }

        if (this.selectedProducts.length === 0) {
          this.applyResidentChange(resident);
          return;
        }

        this.suppressChange = true;
        this.residentControl.setValue(this.previousResident, {
          emitEvent: false,
        });
        this.suppressChange = false;

        this.openListConfirmationModal(
          this.translateService.instant(
            'listOfNeeds.changeResidentConfirmationMessage'
          ),
          'Wechseln',
          true,
          () => this.applyResidentChange(resident)
        );
      });
  }

  private loadInitialResident(): void {
    this.shopSelectionStore.resident$.pipe(take(1)).subscribe({
      next: resident => {
        this.residentControl.setValue(resident);
        this.previousResident = resident;
      },
    });
  }

  private setupBackdropClick(): void {
    this.dialogRef
      .backdropClick()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.closeDialog();
      });
  }

  ngOnDestroy(): void {
    this.stickyObserver?.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupStickyObserver(): void {
    if (
      typeof IntersectionObserver === 'undefined' ||
      !this.listSection?.nativeElement ||
      !this.stickySentinel?.nativeElement
    ) {
      return;
    }

    this.stickyObserver = new IntersectionObserver(
      ([entry]) => {
        this.isStickyTopStuck = entry.intersectionRatio < 1;
      },
      {
        root: this.listSection.nativeElement,
        threshold: [1],
      }
    );

    this.stickyObserver.observe(this.stickySentinel.nativeElement);
  }

  private applyResidentChange(resident: Resident): void {
    this.residentControl.setValue(resident, { emitEvent: false });
    this.previousResident = resident;
    this.residentCart = this.cartService.cart.getResidentCart(resident.id);
    this.shopSelectionService.selectResident(resident.id);
    this.selectedProducts = [];
    this.updateListTotal();
  }

  private openListConfirmationModal(
    message: any,
    buttonText: string,
    showCancelButton = true,
    action?: () => void
  ): void {
    const dialogConfig = this.buildConfirmationDialogConfig(
      message,
      buttonText,
      showCancelButton,
      action
    );
    this.dialog.open(ConfirmationModalComponent, dialogConfig);
  }

  private buildConfirmationDialogConfig(
    message: any,
    buttonText: string,
    showCancelButton = true,
    action?: () => void
  ): ConfirmationDialogProps {
    return {
      width: '600px',
      autoFocus: false,
      data: {
        message: message || 'Die Liste ist nicht leer. Sind Sie sicher?',
        closable: false,
        buttons: [
          ...(showCancelButton
            ? [
                {
                  text: 'Abbrechen',
                  id: 0,
                  closeDialog: true,
                  color: '#42405F',
                },
              ]
            : []),
          {
            text: buttonText,
            id: 1,
            ...(showCancelButton ? {} : { closeDialog: true }),
            action,
            color: '#ff6100',
          },
        ],
      },
    };
  }

  closeDialog() {
    if (this.selectedProducts.length === 0) {
      this.dialogRef.close();
      return;
    }
    this.openListConfirmationModal(
      this.translateService.instant(
        'listOfNeeds.closeDialogConfirmationMessage'
      ),
      'Zurück zum Shop',
      true,
      () => this.dialogRef.close()
    );
  }

  updateListTotal() {
    const { total, count } = this.selectedProducts.reduce(
      (acc, p) => {
        if (p.valid && p.product) {
          return {
            total: acc.total + (p.product.price || 0) * p.quantity,
            count: acc.count + p.quantity,
          };
        }
        return acc;
      },
      { total: 0, count: 0 }
    );
    this.listTotal = total;
    this.listNumberOfItems = count;
  }

  onProductSelected(productId: number) {
    this.productsService
      .getProductsByIds([productId])
      .pipe(take(1))
      .subscribe({
        next: products => {
          if (!products?.data || products.data.length === 0) {
            this.handleProductFetchFailure(productId);
            return;
          }
          this.handleProductFetchSuccess(productId, products.data[0]);
        },
        error: err => {
          console.error('Error fetching product by ID:', err);
        },
      });
  }

  private handleProductFetchFailure(productId: number): void {
    this.selectedProducts = [
      {
        product: { id: productId } as Product,
        quantity: 0,
        valid: false,
      },
      ...this.selectedProducts,
    ];
  }

  private handleProductFetchSuccess(productId: number, product: Product): void {
    const foundIndex = this.selectedProducts.findIndex(
      p => p.product?.id === productId
    );
    if (foundIndex !== -1) {
      this.incrementProductQuantity(foundIndex, product);
      return;
    }

    this.addNewProduct(product);
  }

  private incrementProductQuantity(
    productIndex: number,
    product: Product
  ): void {
    const currentQty = this.selectedProducts[productIndex].quantity || 0;
    this.updateQuantity({
      product,
      quantity: currentQty + 1,
    });
  }

  private addNewProduct(product: Product): void {
    this.selectedProducts = [
      {
        product,
        quantity: 1,
        valid: true,
      },
      ...this.selectedProducts,
    ];
    this.updateListTotal();
  }

  onProductRemoved(productId: number) {
    this.selectedProducts = this.selectedProducts.filter(
      p => p.product?.id !== productId
    );
    this.updateListTotal();
  }

  updateQuantity(event: { product: Product; quantity: number }) {
    const { product, quantity } = event;

    if (quantity <= 0) {
      this.removeProduct(product.id);
      return;
    }

    const existing = this.selectedProducts.find(
      p => p.product?.id === product.id
    );

    if (existing) {
      this.updateExistingProduct(existing, quantity);
      return;
    }

    this.addNewProductQuantity(product, quantity);
  }

  private removeProduct(productId: number): void {
    this.selectedProducts = this.selectedProducts.filter(
      p => p.product?.id !== productId
    );
    this.updateListTotal();
  }

  private updateExistingProduct(
    product: { product: Product | null; quantity: number; valid: boolean },
    quantity: number
  ): void {
    product.quantity = quantity;
    this.updateListTotal();
  }

  private addNewProductQuantity(product: Product, quantity: number): void {
    const alternativeIndex = this.selectedProducts.findIndex(
      p => p.product?.alternativeProductId === product.id
    );

    const newEntry = {
      product,
      quantity,
      valid: true,
    };

    if (alternativeIndex !== -1) {
      this.insertProductAfterAlternative(alternativeIndex, newEntry);
    } else {
      this.selectedProducts = [newEntry, ...this.selectedProducts];
    }

    this.updateListTotal();
  }

  private insertProductAfterAlternative(
    alternativeIndex: number,
    newEntry: { product: Product; quantity: number; valid: boolean }
  ): void {
    const newArray = [...this.selectedProducts];
    newArray.splice(alternativeIndex + 1, 0, newEntry);
    this.selectedProducts = newArray;
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.listSection?.nativeElement;
      if (!el) {
        return;
      }

      el.scrollTo({
        top: el.scrollHeight,
        behavior: 'smooth',
      });
    });
  }

  addProductsToCart() {
    if (this.hasUnavailableSelectedProducts()) {
      this.showUnavailableProductsMessage();
      return;
    }

    this.cartService.setUpdate();
    this.ensureResidentCart();
    this.addAllProductsToCart();
    this.scrollToBottom();
    this.showSuccessMessage();
    this.resetProducts();
  }

  private hasUnavailableSelectedProducts(): boolean {
    return this.selectedProducts.some(
      p => p.valid && p.product && p.product.isUnavailable()
    );
  }

  private showUnavailableProductsMessage(): void {
    this.openListConfirmationModal(
      this.translateService.instant('listOfNeeds.unavailableProductsMessage', {
        residentName: `${handleGender(this.residentControl.value.gender)} ${this.residentControl.value.fname} ${this.residentControl.value.lname}`,
      }),
      'Ok',
      false
    );
  }

  private ensureResidentCart(): void {
    if (!this.residentCart) {
      this.residentCart = this.cartService.cart.createNewResidentCart(
        this.residentControl.value.id
      );
    }
  }

  private addAllProductsToCart(): void {
    this.selectedProducts.forEach(product => {
      if (!this.isProductValid(product)) {
        return;
      }
      this.addProductToCart(product);
    });
  }

  private isProductValid(product: {
    product: Product | null;
    quantity: number;
    valid: boolean;
  }): boolean {
    return (
      product.product &&
      product.valid === true &&
      !product.product.isUnavailable()
    );
  }

  private addProductToCart(product: {
    product: Product;
    quantity: number;
    valid: boolean;
  }): void {
    const residentId = this.residentControl.value.id;

    if (!this.residentCart.getProduct(product.product.id)) {
      this.cartService.cart.addProductToResidentCart(
        residentId,
        product.product.convertToCartProduct()
      );
    }

    const existingQuantity =
      this.residentCart.products.find(p => p.id === product.product.id)
        ?.quantity || 0;

    this.cartService.cart.changeResidentCartProductQuantity(
      residentId,
      product.product.id,
      product.quantity + existingQuantity
    );
  }

  private showSuccessMessage(): void {
    const productCount = this.selectedProducts.filter(
      p => p.valid && p.product
    ).length;
    this.snackbarService.success(
      `${productCount} Produkte zur Tüte Hinzugefügt`
    );
  }

  private resetProducts(): void {
    this.selectedProducts = [];
    this.updateListTotal();
  }
}
